import { useCallback } from 'react'
import { v4 as generateUuid } from 'uuid'

import { getCategoryKeyFromRawCategory } from '../../app/_components/pages/organization/constants'
import { useOrganization } from '../../app/_providers/OrganizationProvider.client'
import { CartContainer, UseCartNewCartItem } from '../../brunswick/containers/cart'
import { useMakeCartItem } from '../../brunswick/containers/cart/utils'

import { useEmbroidery } from './EmbroideryProvider'
import { hasLogoSetupLineForLogo } from './utils/unorganized'

export const useMakeEmbroideredCartItem = () => {
  const { cart } = CartContainer.useContainer()
  const { embroiderySelectionsEnabledForBag, embroideryLineItemProperties } = useEmbroidery()
  const { hasMandatoryLogoEmbroidery } = useOrganization()
  const makeCartItem = useMakeCartItem()

  return useCallback(
    (
      props: Parameters<ReturnType<typeof useMakeCartItem>>[0],
      categoryId: string
    ): ReturnType<ReturnType<typeof useMakeCartItem>> => {
      const isB2bLogo = hasMandatoryLogoEmbroidery({
        categoryId,
        categoryKey: getCategoryKeyFromRawCategory(categoryId),
      })

      const hasSelectedEmbroidery =
        Object.keys(embroiderySelectionsEnabledForBag).length > 0 &&
        embroiderySelectionsEnabledForBag.embroideryItemVariant

      if (
        hasSelectedEmbroidery &&
        embroideryLineItemProperties &&
        embroiderySelectionsEnabledForBag.embroideryItemVariant
      ) {
        const cartItems: UseCartNewCartItem[] = []
        // Only treat setup as present if the actual setup line item exists for this logo.
        const logoEmbroiderySetupInCart = hasLogoSetupLineForLogo(
          cart.items,
          embroideryLineItemProperties._logo_id
        )

        const variantShopifyId = 'item' in props ? props.item.shopifyId : props.variant.shopifyId

        const embroideryPropertiesWithUniqueGuid = {
          ...embroideryLineItemProperties,
          _guid: generateUuid(),
        }

        cartItems.push(
          ...makeCartItem({
            variant: {
              ...embroiderySelectionsEnabledForBag.embroideryItemVariant.productInfo,
            },
            variantGroup: embroiderySelectionsEnabledForBag.embroideryItemVariant.productInfo.group,
            product:
              embroiderySelectionsEnabledForBag.embroideryItemVariant.productInfo.group.product,
            options: {
              properties: {
                ...embroideryPropertiesWithUniqueGuid,
                ...(!logoEmbroiderySetupInCart &&
                  embroiderySelectionsEnabledForBag.embroiderySetupVariant && {
                    _logo_setup_fee:
                      embroiderySelectionsEnabledForBag.embroiderySetupVariant.productInfo.price.amount.toString(),
                  }),
              },
              quantity: props.options.quantity,
              attributedPortal: props.options.attributedPortal
                ? {
                    ...props.options.attributedPortal,
                    itemType: isB2bLogo ? 'PortalLogo' : 'Other',
                  }
                : undefined,
            },
          })
        )

        const cartItemOptions = {
          ...props.options,
          properties: {
            ...props.options.properties,
            _embroideryId: variantShopifyId,
            _guid: embroideryPropertiesWithUniqueGuid._guid,
            _LPROP: embroideryPropertiesWithUniqueGuid._LPROP,
            _logo_id: embroideryPropertiesWithUniqueGuid._logo_id,
          },
        }

        if ('item' in props) {
          cartItems.push(
            ...makeCartItem({
              ...props,
              finalSale: true,
              options: cartItemOptions,
            })
          )
        } else {
          cartItems.push(
            ...makeCartItem({
              ...props,
              variantGroup: {
                ...props.variantGroup,
                finalSale: true,
              },
              options: cartItemOptions,
            })
          )
        }

        if (
          embroideryLineItemProperties._logo_setup_embroideryId &&
          embroiderySelectionsEnabledForBag.embroiderySetupVariant
        ) {
          if (!logoEmbroiderySetupInCart) {
            cartItems.push(
              ...makeCartItem({
                variant: {
                  ...embroiderySelectionsEnabledForBag.embroiderySetupVariant.productInfo,
                },
                variantGroup:
                  embroiderySelectionsEnabledForBag.embroiderySetupVariant.productInfo.group,
                product:
                  embroiderySelectionsEnabledForBag.embroiderySetupVariant.productInfo.group
                    .product,
                options: {
                  properties: {
                    _embroidery_version: embroideryLineItemProperties._embroidery_version,
                    _scrub_color: embroideryLineItemProperties._scrub_color,
                    _logo_id: embroideryLineItemProperties._logo_id,
                    _guid: embroideryLineItemProperties._logo_setup_guid,
                    _logo_preview: embroideryLineItemProperties._logo_preview,
                    logo_name: embroideryLineItemProperties.logo_name,
                    logo_placement: embroideryLineItemProperties.logo_placement,
                    logo_color_option: embroideryLineItemProperties.logo_color_option,
                    _is_logo_setup: 'true',
                  },
                  quantity: 1,
                  attributedPortal: props.options.attributedPortal
                    ? {
                        ...props.options.attributedPortal,
                        itemType: 'Other',
                      }
                    : undefined,
                },
              })
            )
          }
        }

        return cartItems
      } else {
        return makeCartItem(props)
      }
    },
    [
      hasMandatoryLogoEmbroidery,
      embroiderySelectionsEnabledForBag,
      embroideryLineItemProperties,
      cart.items,
      makeCartItem,
    ]
  )
}
